import { createApi } from '@reduxjs/toolkit/query/react';
import { createBackendBaseQuery, transformApiError, withCountryHeader } from '../../../utils/createAuthBaseQuery';
import { API_URLS } from '../../../constants/apiUrl';

// Mirrors the backend ElektronikonSolutionSearchResultDto.
export type ElektronikonYesNo = 'Yes' | 'No';

// Each configuration factor is returned as "Yes" (selected) or "No" (not selected).
export interface ElektronikonConfigurationFactors {
  energyRecovery?: ElektronikonYesNo;
  antiCondensationProtection?: ElektronikonYesNo;
  externalSpeedControl?: ElektronikonYesNo;
  osci?: ElektronikonYesNo;
  highAmbientTemperature?: ElektronikonYesNo;
  antiCondensationThermistor?: ElektronikonYesNo;
  integratedDryers?: ElektronikonYesNo;
  ddFilter?: ElektronikonYesNo;
  tubeCooler?: ElektronikonYesNo;
  pT1000?: ElektronikonYesNo;
  pT100?: ElektronikonYesNo;
  phaseSequenceRelay?: ElektronikonYesNo;
  electronicCondensateDrain?: ElektronikonYesNo;
  spmMonitoring?: ElektronikonYesNo;
}

// The array fields hold every value available for the matched controller, not the
// machine's own selection.
export interface ElektronikonSolutionResponse {
  oldControllerPartNumber?: number;
  currentController?: string;
  modelName?: string[];
  productRange?: string[];
  converter?: string[];
  region?: string[];
  configurationFactors?: ElektronikonConfigurationFactors;
}

// Mirrors the backend SearchElektronikonSolutionRequest. Property names bind
// case-insensitively to the request object (e.g. serialNumber -> SerialNumber).
export interface FetchElektronikonSolutionParams {
  controllerPartNumber?: number;
  serialNumber?: string;
  elektronikonSolutionLogId?: number;
}

export interface ElektronikonPartToOrder {
  partNumber: string;
  component: string;
  quantity: number;
}

// Returned by Solution/Details for the machine configuration picked on the results page.
export interface ElektronikonSolutionDetailsResponse {
  recommendedUpgrade?: string;
  partNumber?: number;
  partsToOrder?: ElektronikonPartToOrder[] | null;
  feedbackReceived?: boolean | null;
  instruction?: string | null;
  serviceDiagram?: string | null;
  databaseRef?: string | null;
  remarks?: string | null;
}

// The controller and the machine configuration selected in Machine Information.
export interface FetchElektronikonSolutionDetailsParams
  extends ElektronikonConfigurationFactors {
  oldControllerPartNumber: number;
  modelName: string;
  productRange: string;
  converter: string;
  region: string;
}

const BASE_URL = process.env.REACT_APP_ELEKTRONIKON_SERVICE_API_BASE_URL as string;

if (!BASE_URL) {
  throw new Error('REACT_APP_ELEKTRONIKON_SERVICE_API_BASE_URL environment variable is not set');
}

const baseQuery = createBackendBaseQuery(BASE_URL);

// Attach the user's country header to the Elektronikon Solution/Search request.
const baseQueryWithCountry = withCountryHeader(
  baseQuery,
  (url) => url.includes(API_URLS.ELEKTRONIKON.SOLUTION_SEARCH),
  'elektronikonSolutionApi'
);

export const elektronikonSolutionApiSlice = createApi({
  reducerPath: 'elektronikonSolutionApi',
  baseQuery: baseQueryWithCountry,
  tagTypes: ['ElektronikonSolution'],
  endpoints(builders) {
    return {
      fetchElektronikonSolution: builders.query<
        ElektronikonSolutionResponse,
        FetchElektronikonSolutionParams
      >({
        query: (params) => {
          let queryString = '';
          if (params) {
            const usp = new URLSearchParams();
            Object.entries(params).forEach(([key, value]) => {
              if (value !== undefined && value !== null && value !== '') {
                usp.append(key, String(value));
              }
            });
            queryString = usp.toString() ? `?${usp.toString()}` : '';
          }
          return {
            url: `${API_URLS.ELEKTRONIKON.SOLUTION_SEARCH}${queryString}`,
            method: 'GET',
          };
        },
        providesTags: ['ElektronikonSolution'],
        transformErrorResponse: transformApiError,
      }),
      fetchElektronikonSolutionDetails: builders.query<
        ElektronikonSolutionDetailsResponse,
        FetchElektronikonSolutionDetailsParams
      >({
        query: (params) => {
          const usp = new URLSearchParams();
          Object.entries(params).forEach(([key, value]) => {
            if (value !== undefined && value !== null && value !== '') {
              usp.append(key, String(value));
            }
          });
          return {
            url: `${API_URLS.ELEKTRONIKON.SOLUTION_DETAILS}?${usp.toString()}`,
            method: 'GET',
          };
        },
        providesTags: ['ElektronikonSolution'],
        transformErrorResponse: transformApiError,
      }),
    };
  },
});

export const {
  useFetchElektronikonSolutionQuery,
  useLazyFetchElektronikonSolutionQuery,
  useFetchElektronikonSolutionDetailsQuery,
} = elektronikonSolutionApiSlice;
